import React, { useState, useMemo } from 'react';
import { useMixTemplates } from '@hooks/useMixTemplates';
import { MixTemplate } from '@/types/production.types';
import MixTemplateCard from './MixTemplateCard';
import { Modal, Input, Button } from '../ui';
import { Search, Sliders } from 'lucide-react';

interface MixTemplateBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: MixTemplate) => void;
  selectedId?: string | null;
}

const MixTemplateBrowser: React.FC<MixTemplateBrowserProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedId,
}) => {
  const { data: mixTemplates } = useMixTemplates();
  const [searchTerm, setSearchTerm] = useState('');
  const [genreFilter, setGenreFilter] = useState<string | null>(null);

  const allGenres = useMemo(() => {
    const set = new Set<string>();
    mixTemplates.forEach(t => t.genre.forEach(g => set.add(g)));
    return Array.from(set).sort();
  }, [mixTemplates]);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return mixTemplates.filter(template => {
      const matchesSearch = !term || [
        template.name,
        template.description,
        ...template.genre,
      // Same guard as InstrumentForge ProfileBrowser: DB rows may lack a field.
      ].some(field => typeof field === 'string' && field.toLowerCase().includes(term));

      const matchesGenre = !genreFilter || template.genre.includes(genreFilter);

      return matchesSearch && matchesGenre;
    });
  }, [mixTemplates, searchTerm, genreFilter]);

  const totalCount = mixTemplates.length;

  const handleSelect = (template: MixTemplate) => {
    onSelect(template);
    onClose();
    setSearchTerm('');
    setGenreFilter(null);
  };

  const handleClose = () => {
    onClose();
    setSearchTerm('');
    setGenreFilter(null);
  };

  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="p-2.5 bg-accent/20 rounded-xl text-accent">
        <Sliders size={20} />
      </div>
      <div>
        <span className="text-xl font-black text-slate-50 uppercase tracking-tighter">
          Select Mix Template
        </span>
        <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-1">
          Browse stereo positioning blueprints
        </p>
      </div>
    </div>
  );

  const footerContent = (
    <div className="flex justify-between items-center">
      <p className="text-[11px] text-slate-400 font-bold uppercase">
        Showing {filtered.length} of {totalCount} templates
      </p>
      <Button variant="secondary" size="sm" onClick={handleClose}>
        Cancel
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      size="full"
      footer={footerContent}
      className="max-h-[85vh] flex flex-col rounded-3xl"
    >
      {/* Custom Header */}
      <div className="px-2 pb-5 border-b border-slate-800 -mt-2 mb-4">
        {headerContent}
      </div>

      {/* Search + Genre Filter */}
      <div className="pb-5 border-b border-slate-800 mb-4 space-y-3">
        <Input
          placeholder="Search by name, genre, description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leftElement={<Search size={18} className="text-slate-400" />}
          autoFocus
          className="rounded-2xl py-3.5"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Genre</span>
          <button
            onClick={() => setGenreFilter(null)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
              !genreFilter
                ? 'bg-accent/20 text-accent border border-accent/30'
                : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:text-slate-300'
            }`}
          >
            All
          </button>
          {allGenres.map(genre => (
            <button
              key={genre}
              onClick={() => setGenreFilter(genreFilter === genre ? null : genre)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
                genreFilter === genre
                  ? 'bg-accent/20 text-accent border border-accent/30'
                  : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:text-slate-300'
              }`}
            >
              {genre}
            </button>
          ))}
        </div>
      </div>

      {/* Card Grid */}
      <div className="flex-grow overflow-y-auto custom-scrollbar -mx-2 px-2">
        {filtered.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-700">
            <Sliders size={48} className="opacity-20 mb-4" />
            <p className="font-black uppercase tracking-widest text-sm">No matching templates</p>
            <p className="text-[11px] text-slate-400 mt-2">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(template => (
              <MixTemplateCard
                key={template.id}
                template={template}
                isSelected={selectedId === template.id}
                onClick={() => handleSelect(template)}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MixTemplateBrowser;
